import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, Typography, CircularProgress, Chip, List, ListItem, ListItemText, Divider, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Menu, MenuItem, Grid,  } from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';
import Pagination from '../../components/Pagination';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const themeColors = {
  gold: '#d4af37',
  goldLight: '#f4e4bc',
  dark: '#1a1a2e',
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('fa-IR').format(num || 0);
};

const InvoicesList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const navigate = useNavigate();

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
      };
      if (search) params.search = search;
      
      const response = await client.get('/orders/invoices/', { params });
      setInvoices(response.data.results || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error('خطا در دریافت فاکتورها');
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchInvoices();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, pageSize]);

  const handleViewDetail = async (invoice) => {
    try {
      const response = await client.get(`/orders/invoices/${invoice.id}/`);
      setSelectedInvoice(response.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error('خطا در دریافت جزئیات');
    }
    setMenuAnchor(null);
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await client.get(`/orders/invoices/${invoice.id}/pdf/`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('فایل PDF دانلود شد');
    } catch (error) {
      toast.error('خطا در دانلود PDF');
    }
    setMenuAnchor(null);
  };

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        فاکتورها
      </Typography>

      <CustomTextField         fullWidth
        placeholder="جستجو..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress sx={{ color: themeColors.gold }} />
        </Box>
      ) : invoices.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">فاکتوری یافت نشد</Typography>
        </Card>
      ) : (
        <>
          <Card>
            <List disablePadding>
              {invoices.map((invoice, index) => (
                <React.Fragment key={invoice.id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    onClick={() => handleViewDetail(invoice)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight="bold">
                            {toPersianDigits(invoice.invoice_number)}
                          </Typography>
                          <Chip
                            label={`${toPersianDigits(invoice.items_count)} قلم`}
                            size="small"
                            sx={{ bgcolor: themeColors.goldLight }}
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" component="span">
                            {invoice.customer_name || 'بدون مشتری'}
                          </Typography>
                          <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                            • سفارش: {toPersianDigits(invoice.order_number)}
                          </Typography>
                        </>
                      }
                    />
                    <Box sx={{ textAlign: 'left', minWidth: 100 }}>
                      <Typography variant="body2" fontWeight="bold" color={themeColors.gold}>
                        {formatNumber(invoice.total_amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ریال
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedInvoice(invoice);
                        setMenuAnchor(e.currentTarget);
                      }}
                    >
                      <MoreIcon />
                    </IconButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Card>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </>
      )}

      {/* Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleViewDetail(selectedInvoice)}>
          مشاهده جزئیات
        </MenuItem>
        <MenuItem onClick={() => handleDownloadPdf(selectedInvoice)}>
          <PdfIcon sx={{ mr: 1, fontSize: 20 }} />
          دانلود PDF
        </MenuItem>
      </Menu>

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ bgcolor: themeColors.dark, color: themeColors.gold }}>
          فاکتور {selectedInvoice && toPersianDigits(selectedInvoice.invoice_number)}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedInvoice && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">مشتری</Typography>
                  <Typography>{selectedInvoice.customer_name || 'بدون مشتری'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">موبایل</Typography>
                  <Typography dir="ltr" sx={{ textAlign: 'right' }}>
                    {toPersianDigits(selectedInvoice.customer_mobile)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">سفارش</Typography>
                  <Typography>{toPersianDigits(selectedInvoice.order_number)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">فروشگاه</Typography>
                  <Typography>{selectedInvoice.shop_name}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                اقلام فاکتور
              </Typography>
              {selectedInvoice.items?.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {item.item_name}
                      {item.description && ` - ${item.description}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {toPersianDigits(item.quantity)} {item.unit_type_display} × {formatNumber(item.unit_price)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {formatNumber(item.total_price)} ریال
                  </Typography>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                p: 2, 
                bgcolor: themeColors.goldLight,
                borderRadius: 1,
              }}>
                <Typography fontWeight="bold" variant="h6">جمع کل:</Typography>
                <Typography fontWeight="bold" variant="h6" color={themeColors.dark}>
                  {formatNumber(selectedInvoice.total_amount)} ریال
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            startIcon={<PdfIcon />}
            onClick={() => handleDownloadPdf(selectedInvoice)}
            sx={{ color: themeColors.gold }}
          >
            دانلود PDF
          </Button>
          <Button onClick={() => setDetailOpen(false)}>بستن</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoicesList;
